"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import type { CategoryFacets, Facet, FacetCheckbox, FacetRange } from "@/lib/api-types";

// ---------------------------------------------------------------------------
// Контекст: один state, два потребителя — body (<CategoryFilters>) и CTA
// (<CategoryFiltersCTA>). Mobile drawer рендерит body внутри scrollable
// области, а CTA — в отдельном footer'е снаружи (фикс для Safari 14).
// ---------------------------------------------------------------------------

interface State {
  priceMin: number | null;
  priceMax: number | null;
  checkbox: Record<string, string[]>;
  range: Record<string, [number | null, number | null]>;
}

interface FilterCtx {
  facets: CategoryFacets;
  state: State;
  setState: Dispatch<SetStateAction<State>>;
  apply: () => void;
  reset: () => void;
  hasAnyApplied: boolean;
  isPending: boolean;
  totalAfterFilter: number;
  alwaysShowSlider: boolean;
}

const FilterContext = createContext<FilterCtx | null>(null);

function useFilterCtx(): FilterCtx {
  const v = useContext(FilterContext);
  if (!v) throw new Error("CategoryFiltersProvider missing");
  return v;
}

/**
 * Провайдер фильтров — оборачивает body + CTA. Внутри один state.
 * Использовать:
 *   <CategoryFiltersProvider facets={...} totalAfterFilter={...}>
 *     <CategoryFilters />           ← список секций (scrollable)
 *     <CategoryFiltersCTA />        ← кнопка «Показать N» (где угодно в DOM)
 *   </CategoryFiltersProvider>
 */
export function CategoryFiltersProvider({
  facets,
  totalAfterFilter,
  alwaysShowSlider = false,
  children,
}: {
  facets: CategoryFacets;
  totalAfterFilter: number;
  alwaysShowSlider?: boolean;
  children: ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const initial = useMemo(() => paramsToState(params), [params]);
  const [state, setState] = useState<State>(initial);

  const hasAnyApplied = useMemo(
    () =>
      state.priceMin != null ||
      state.priceMax != null ||
      Object.values(state.checkbox).some((v) => v.length) ||
      Object.values(state.range).some(([a, b]) => a != null || b != null),
    [state],
  );

  const apply = () => {
    const next = new URLSearchParams();
    const sort = params.get("sort");
    if (sort) next.set("sort", sort);
    if (state.priceMin != null) next.set("price_min", String(state.priceMin));
    if (state.priceMax != null) next.set("price_max", String(state.priceMax));
    for (const [key, vals] of Object.entries(state.checkbox)) {
      if (vals.length) next.append("f", `${key}|${vals.join(",")}`);
    }
    for (const [key, [mn, mx]] of Object.entries(state.range)) {
      if (mn != null || mx != null) {
        next.append("f", `${key}|${mn ?? ""}..${mx ?? ""}`);
      }
    }
    startTransition(() => {
      router.push(`${pathname}?${next.toString()}`, { scroll: false });
    });
  };

  const reset = () => {
    setState({ priceMin: null, priceMax: null, checkbox: {}, range: {} });
    const next = new URLSearchParams();
    const sort = params.get("sort");
    if (sort) next.set("sort", sort);
    startTransition(() => {
      router.push(`${pathname}?${next.toString()}`, { scroll: false });
    });
  };

  return (
    <FilterContext.Provider
      value={{
        facets,
        state,
        setState,
        apply,
        reset,
        hasAnyApplied,
        isPending,
        totalAfterFilter,
        alwaysShowSlider,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Body (список секций) — без CTA. Один компонент для десктопного sidebar'а
// и мобильного drawer body.
// ---------------------------------------------------------------------------

interface BodyProps {
  /** Сколько секций показать сразу. Остальные — под кнопкой «Все фильтры». */
  visibleTop?: number;
}

export function CategoryFilters({ visibleTop = 6 }: BodyProps = {}) {
  const { facets, state, setState, alwaysShowSlider } = useFilterCtx();

  const removePriceFromState = () =>
    setState((s) => ({ ...s, priceMin: null, priceMax: null }));
  const removeRangeFromState = (key: string) =>
    setState((s) => {
      const r = { ...s.range };
      delete r[key];
      return { ...s, range: r };
    });
  const removeCheckboxFromState = (key: string) =>
    setState((s) => {
      const c = { ...s.checkbox };
      delete c[key];
      return { ...s, checkbox: c };
    });

  // Сохраняем ИСХОДНЫЙ порядок фасет — активные не поднимаем наверх
  // (это «прыгало» при клике, см. пользовательский фидбек).
  // Топ-N всегда видны, «хвост» — под кнопкой «Все фильтры (+M)».
  const { topFacets, restFacets, hasAppliedInRest } = useMemo(() => {
    const applied = new Set<string>();
    for (const [k, v] of Object.entries(state.checkbox)) {
      if (v.length) applied.add(k);
    }
    for (const [k, v] of Object.entries(state.range)) {
      if (v[0] != null || v[1] != null) applied.add(k);
    }
    const ordered = facets.facets;
    const top = ordered.slice(0, visibleTop);
    const rest = ordered.slice(visibleTop);
    return {
      topFacets: top,
      restFacets: rest,
      // Если активный фильтр оказался в скрытой части — авто-раскрываем,
      // иначе юзер потеряет видимость активного фильтра.
      hasAppliedInRest: rest.some((f) => applied.has(f.key)),
    };
  }, [facets.facets, state, visibleTop]);

  const [showAll, setShowAll] = useState(false);
  // Если активен фильтр из хвоста — разворачиваем «Все фильтры» автоматически.
  useEffect(() => {
    if (hasAppliedInRest) setShowAll(true);
  }, [hasAppliedInRest]);

  return (
    <div className="text-sm">
      <div className="divide-y divide-border">
        <PriceBlock
          facet={facets.price}
          min={state.priceMin}
          max={state.priceMax}
          onChange={(mn, mx) =>
            setState((s) => ({ ...s, priceMin: mn, priceMax: mx }))
          }
          onClear={removePriceFromState}
          alwaysShowSlider={alwaysShowSlider}
        />
        {topFacets.map((f) => (
          <FacetBlock
            key={f.key}
            facet={f}
            state={state}
            setState={setState}
            onClearRange={() => removeRangeFromState(f.key)}
            onClearCheckbox={() => removeCheckboxFromState(f.key)}
            alwaysShowSlider={alwaysShowSlider}
          />
        ))}
        {showAll &&
          restFacets.map((f) => (
            <FacetBlock
              key={f.key}
              facet={f}
              state={state}
              setState={setState}
              onClearRange={() => removeRangeFromState(f.key)}
              onClearCheckbox={() => removeCheckboxFromState(f.key)}
              alwaysShowSlider={alwaysShowSlider}
            />
          ))}
      </div>

      {restFacets.length > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-[10px] border border-border bg-white px-4 py-2.5 text-[13px] font-medium text-text transition-colors hover:bg-bg-muted"
        >
          {showAll ? "Скрыть фильтры" : `Все фильтры (+${restFacets.length})`}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            className="text-text-subtle"
            style={{
              transform: showAll ? "rotate(180deg)" : "none",
              transition: "transform 0.15s",
            }}
          >
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Отдельная CTA — может рендериться в любом месте дерева внутри провайдера.
 */
export function CategoryFiltersCTA() {
  const { apply, reset, hasAnyApplied, isPending, totalAfterFilter } = useFilterCtx();
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={apply}
        disabled={isPending}
        className="h-11 flex-1 rounded-[10px] bg-brand-600 px-4 text-sm font-medium text-white transition-colors hover:bg-brand-700 active:opacity-80 disabled:opacity-60"
      >
        {isPending
          ? "Применяю…"
          : totalAfterFilter > 0
          ? `Показать ${formatCount(totalAfterFilter)}`
          : "Применить"}
      </button>
      {hasAnyApplied && (
        <button
          type="button"
          onClick={reset}
          disabled={isPending}
          aria-label="Сбросить все фильтры"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border border-border text-text-muted transition-colors hover:bg-bg-muted hover:text-text disabled:opacity-60"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function paramsToState(params: URLSearchParams): State {
  const out: State = {
    priceMin: numParam(params.get("price_min")),
    priceMax: numParam(params.get("price_max")),
    checkbox: {},
    range: {},
  };
  for (const raw of params.getAll("f")) {
    const idx = raw.indexOf("|");
    if (idx <= 0) continue;
    const key = raw.slice(0, idx);
    const val = raw.slice(idx + 1);
    if (val.includes("..")) {
      const [mn, mx] = val.split("..", 2);
      out.range[key] = [numParam(mn), numParam(mx)];
    } else {
      out.checkbox[key] = val.split(",").filter(Boolean);
    }
  }
  return out;
}

function numParam(v: string | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatCount(n: number): string {
  return n.toLocaleString("ru-RU").replace(",", " ");
}

function fmtNum(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 100) / 100);
}

function rangeSummary(min: number | null, max: number | null): string | null {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${fmtNum(min)}–${fmtNum(max)}`;
  if (min != null) return `от ${fmtNum(min)}`;
  return `до ${fmtNum(max!)}`;
}

// ----------------------------------------------------------------------------

function PriceBlock({
  facet,
  min,
  max,
  onChange,
  onClear,
  alwaysShowSlider,
}: {
  facet: FacetRange;
  min: number | null;
  max: number | null;
  onChange: (mn: number | null, mx: number | null) => void;
  onClear: () => void;
  alwaysShowSlider?: boolean;
}) {
  const summary = rangeSummary(min, max);
  return (
    <Section
      title="Цена"
      suffix="₽"
      summary={summary}
      onClear={summary ? onClear : undefined}
      defaultOpen={summary != null}
    >
      <DualRange
        absMin={facet.min}
        absMax={facet.max}
        min={min}
        max={max}
        onChange={onChange}
        alwaysShowSlider={alwaysShowSlider}
      />
    </Section>
  );
}

function FacetBlock({
  facet,
  state,
  setState,
  onClearRange,
  onClearCheckbox,
  alwaysShowSlider,
}: {
  facet: Facet;
  state: State;
  setState: Dispatch<SetStateAction<State>>;
  onClearRange: () => void;
  onClearCheckbox: () => void;
  alwaysShowSlider?: boolean;
}) {
  if (facet.type === "range") {
    const [mn, mx] = state.range[facet.key] ?? [null, null];
    const summary = rangeSummary(mn, mx);
    return (
      <Section
        title={facet.label}
        summary={summary}
        onClear={summary ? onClearRange : undefined}
        defaultOpen={summary != null}
      >
        <DualRange
          absMin={facet.min}
          absMax={facet.max}
          min={mn}
          max={mx}
          onChange={(nMn, nMx) =>
            setState((s) => ({
              ...s,
              range: { ...s.range, [facet.key]: [nMn, nMx] },
            }))
          }
          alwaysShowSlider={alwaysShowSlider}
        />
      </Section>
    );
  }
  return (
    <CheckboxBlock
      facet={facet}
      state={state}
      setState={setState}
      onClear={onClearCheckbox}
    />
  );
}

function CheckboxBlock({
  facet,
  state,
  setState,
  onClear,
}: {
  facet: FacetCheckbox;
  state: State;
  setState: Dispatch<SetStateAction<State>>;
  onClear: () => void;
}) {
  const selected = state.checkbox[facet.key] ?? [];
  const [showAll, setShowAll] = useState(false);
  const VISIBLE = 4;
  const visible = showAll ? facet.options : facet.options.slice(0, VISIBLE);
  const hidden = facet.options.length - visible.length;
  const isApplied = selected.length > 0;

  const summary = useMemo<string | null>(() => {
    if (!isApplied) return null;
    const head = selected[0];
    const rest = selected.length - 1;
    return rest > 0 ? `${head} +${rest}` : head;
  }, [selected, isApplied]);

  const toggle = (val: string) => {
    setState((s) => {
      const cur = s.checkbox[facet.key] ?? [];
      const next = cur.includes(val)
        ? cur.filter((v) => v !== val)
        : [...cur, val];
      return { ...s, checkbox: { ...s.checkbox, [facet.key]: next } };
    });
  };

  return (
    <Section
      title={facet.label}
      summary={summary}
      onClear={summary ? onClear : undefined}
      defaultOpen={isApplied}
    >
      <ul className="space-y-1.5">
        {visible.map((opt) => {
          const isOn = selected.includes(opt.value);
          return (
            <li key={opt.value}>
              <label className="group flex cursor-pointer items-center gap-2.5 text-sm leading-5 text-text">
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggle(opt.value)}
                  className="h-4 w-4 shrink-0 accent-brand-600"
                />
                <span className="flex-1 group-hover:text-brand-700">
                  {opt.value}
                </span>
                <span className="tabular-nums text-xs text-text-subtle">
                  {opt.count}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {hidden > 0 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1.5 text-xs font-medium text-brand-600 hover:text-brand-700"
        >
          +{hidden} ещё
        </button>
      )}
      {showAll && facet.options.length > VISIBLE && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="mt-1.5 text-xs font-medium text-text-muted hover:text-text"
        >
          Свернуть
        </button>
      )}
    </Section>
  );
}

// ----------------------------------------------------------------------------
// Dual range slider
// ----------------------------------------------------------------------------

function DualRange({
  absMin,
  absMax,
  min,
  max,
  onChange,
  alwaysShowSlider = false,
}: {
  absMin: number;
  absMax: number;
  min: number | null;
  max: number | null;
  onChange: (mn: number | null, mx: number | null) => void;
  alwaysShowSlider?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const safeMin = min ?? absMin;
  const safeMax = max ?? absMax;
  const span = absMax - absMin || 1;
  const leftPct = clamp(((safeMin - absMin) / span) * 100, 0, 100);
  const rightPct = clamp(((safeMax - absMin) / span) * 100, 0, 100);
  const step = useMemo(() => {
    if (span <= 100) return 0.1;
    if (span <= 1000) return 1;
    return Math.max(1, Math.round(span / 200));
  }, [span]);

  const showSlider = alwaysShowSlider || focused || min != null || max != null;

  return (
    <div>
      {showSlider && (
        <div className="relative mb-2.5 h-5">
          <div className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 rounded-full bg-bg-muted" />
          <div
            className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-brand-500"
            style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
          />
          <input
            type="range"
            min={absMin}
            max={absMax}
            step={step}
            value={safeMin}
            onChange={(e) => {
              const v = Number(e.target.value);
              onChange(v === absMin ? null : v, max);
            }}
            aria-label="Минимум"
            className="dual-range pointer-events-none absolute inset-0 w-full appearance-none bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto"
            style={{ zIndex: leftPct > 90 ? 3 : 2 }}
          />
          <input
            type="range"
            min={absMin}
            max={absMax}
            step={step}
            value={safeMax}
            onChange={(e) => {
              const v = Number(e.target.value);
              onChange(min, v === absMax ? null : v);
            }}
            aria-label="Максимум"
            className="dual-range pointer-events-none absolute inset-0 w-full appearance-none bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto"
            style={{ zIndex: 2 }}
          />
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <NumberInput
          value={min}
          placeholder={String(roundForDisplay(absMin))}
          onChange={(v) => onChange(v, max)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        <span className="text-text-subtle">—</span>
        <NumberInput
          value={max}
          placeholder={String(roundForDisplay(absMax))}
          onChange={(v) => onChange(min, v)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      </div>
      <p className="mt-1 text-[11px] leading-tight text-text-subtle">
        доступно {fmtNum(absMin)}–{fmtNum(absMax)}
      </p>
    </div>
  );
}

function NumberInput({
  value,
  placeholder,
  onChange,
  onFocus,
  onBlur,
}: {
  value: number | null;
  placeholder: string;
  onChange: (v: number | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  // Локальный буфер строки чтобы юзер мог ввести «4,» или «4.5» и не терять
  // дробный разделитель пока печатает. До этого `value=number` округляло
  // «4,» → 4 и перерисовывало input без запятой — юзер не мог дойти до «4,5».
  const [buf, setBuf] = useState<string>(
    value == null ? "" : String(value).replace(".", ","),
  );
  const [isFocused, setIsFocused] = useState(false);

  // Если внешний value сменился (slider/clear) и инпут не в фокусе —
  // синхронизируем буфер.
  useEffect(() => {
    if (isFocused) return;
    setBuf(value == null ? "" : String(value).replace(".", ","));
  }, [value, isFocused]);

  return (
    <input
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={buf}
      onFocus={() => {
        setIsFocused(true);
        onFocus?.();
      }}
      onBlur={() => {
        setIsFocused(false);
        onBlur?.();
      }}
      onChange={(e) => {
        // Принимаем кириллический ввод: только цифры, точка и запятая.
        // Запятую сохраняем в буфере, чтобы пользователь видел что набрал.
        const cleaned = e.target.value.replace(/[^\d.,-]/g, "");
        setBuf(cleaned);
        if (!cleaned) {
          onChange(null);
          return;
        }
        // Для parent — нормализованное число (точка как разделитель)
        const normalized = cleaned.replace(",", ".");
        const n = Number(normalized);
        if (Number.isFinite(n) && !normalized.endsWith(".")) {
          onChange(n);
        }
      }}
      className="h-9 w-full min-w-0 rounded-[8px] border border-border bg-white px-2.5 text-sm tabular-nums text-text transition-colors placeholder:text-text-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
    />
  );
}

function roundForDisplay(n: number): number {
  if (Number.isInteger(n)) return n;
  return Math.round(n * 100) / 100;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// ----------------------------------------------------------------------------

function Section({
  title,
  suffix,
  summary,
  defaultOpen = false,
  onClear,
  children,
}: {
  title: string;
  suffix?: string;
  summary?: string | null;
  defaultOpen?: boolean;
  onClear?: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-start gap-2 py-1.5 text-left"
      >
        <span className="flex-1 text-[13px] font-medium leading-snug text-text">
          <span className="line-clamp-2">
            {title}
            {suffix && (
              <span className="ml-1 font-normal text-text-subtle">{suffix}</span>
            )}
          </span>
          {summary && (
            <span className="mt-0.5 block text-xs font-normal text-brand-700">
              {summary}
            </span>
          )}
        </span>
        {onClear && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Очистить"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onClear();
              }
            }}
            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-text-subtle hover:bg-bg-muted hover:text-text"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </span>
        )}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="mt-1 shrink-0 text-text-subtle"
          style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
          }}
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div className="pt-1.5">{children}</div>}
    </div>
  );
}
